/**
 * /me/attendance/excuses — Session 3d PR 21.
 * Owning agent: Khidmat.
 */

import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { session3dApi } from "../lib/session3d-api";
import type { ExcuseItem } from "../lib/session3d-api";
import { MeListPage } from "./_MeListPage";
import type { MeColumn } from "./_MeListPage";
import { useCurrentEmployeeId } from "./_session3dShell";

export function MyExcusesPage() {
  const { t } = useTranslation();
  const employeeId = useCurrentEmployeeId();

  const query = useQuery({
    queryKey: ["s3d-excuses", employeeId],
    queryFn: () => session3dApi.listMyExcuses(employeeId),
    enabled: !!employeeId,
  });

  const items = query.data?.items ?? [];
  const counts = useMemo(() => ({
    total: items.length,
    attention: 0,
    critical: 0,
  }), [items]);

  const columns: MeColumn<ExcuseItem>[] = [
    {
      headerKey: "me.excuses.col.date",
      label: "Date",
      render: (r) => <span dir="ltr" className="text-xs">{r.date}</span>,
    },
    {
      headerKey: "me.excuses.col.type",
      label: "Type",
      render: (r) => <span className="font-medium">{r.excuse_type_name_en}</span>,
    },
    {
      headerKey: "me.excuses.col.duration",
      label: "Duration (min)",
      render: (r) => <span className="font-mono text-xs">{r.duration_minutes}</span>,
    },
    {
      headerKey: "me.excuses.col.reason",
      label: "Reason",
      render: (r) => <span className="text-xs text-text-secondary">{r.reason}</span>,
    },
    {
      headerKey: "me.excuses.col.state",
      label: "State",
      render: (r) => <span className="text-xs">{r.state}</span>,
    },
  ];

  return (
    <MeListPage<ExcuseItem>
      title={t("me.excuses.title", "My Excuses")}
      subtitle={t("me.excuses.subtitle", "Your personal excuse history.")}
      domain="excuses"
      khidmatPrefill={t("me.excuses.ask_khidmat", "How many excuses can I still submit this month?")}
      query={query}
      columns={columns}
      rowKey={(r) => r.request_id}
      briefContext={{
        total: counts.total,
        attention: counts.attention,
        critical: counts.critical,
        top_kind: t("me.excuses.title", "My Excuses"),
        window_label: t("me.excuses.window", "this year"),
      }}
      emptyHeadline={t("me.excuses.empty_headline", "No excuses on record.")}
      emptyBody={t("me.excuses.empty_body", "Khidmat: when you need to file an excuse (late · early leave · permission), it lands here.")}
    />
  );
}
